import {useCallback, useEffect, useRef, useState} from "react";

import {IOption} from "@pg-design/select-module";
import {PreferencesSurveySource} from "@pg-rp/common-types";

import {
    HomeMatchAmplitudeLocation,
    HomeMatchAmplitudeTracker,
    HomeMatchPreferencesWizardAmplitudeButton
} from "@/features/tracking/amplitude/home_match_amplitude_tracker";
import {setPhoneVerificationModalSuppressed} from "@/features/user/state/user_phone_verification_slice";
import {useAppDispatch} from "@/store/hooks/use_app_dispatch";

import {HomeMatchFinancingPreference} from "../../constants/preferences/home_match_preferences_values";
import {getHomeMatchPreferencesWizardPrimaryLabel} from "../../utils/get_home_match_preferences_wizard_primary_label";
import {
    getHomeMatchPreferencesWizardResumeStepIndex,
    getVisibleHomeMatchPreferencesWizardStep,
    HomeMatchPreferencesWizardEntry,
    HomeMatchPreferencesWizardStep,
    HomeMatchPreferencesWizardSurveyStep,
    IHomeMatchPreferencesWizardSurvey,
    isHomeMatchPreferencesWizardParameterStep,
    isHomeMatchPreferencesWizardSurveyStep
} from "../../utils/get_home_match_preferences_wizard_steps";
import {IHomeMatchPreferencesWizardMapActions} from "./use_home_match_preferences_wizard_location_map";
import {useHomeMatchPreferencesWizardPhone} from "./use_home_match_preferences_wizard_phone";
import {useHomeMatchPreferencesWizardSummary} from "./use_home_match_preferences_wizard_summary";
import {useHomeMatchPreferencesWizardSurvey} from "./use_home_match_preferences_wizard_survey";

const {PHONE, SMS, SUMMARY} = HomeMatchPreferencesWizardStep;

interface IParams {
    amplitudeLocation: HomeMatchAmplitudeLocation;
    entry?: HomeMatchPreferencesWizardEntry;
    financingPreference: HomeMatchFinancingPreference | null;
    isOpen: boolean;
    onClose: () => void;
    /** A step opened from the summary. Back and close return there without saving. */
    returnsToSummary?: boolean;
    onSurveyStepsDone: () => void;
    phone: string | null;
    preferencesSurveySource: PreferencesSurveySource;
    regionOptions: IOption<number>[];
    steps: HomeMatchPreferencesWizardStep[];
    survey: IHomeMatchPreferencesWizardSurvey | null;
}

export const useHomeMatchPreferencesWizardSession = (params: IParams) => {
    const {
        amplitudeLocation,
        entry = "homeMatch",
        financingPreference,
        isOpen,
        onClose,
        onSurveyStepsDone,
        returnsToSummary = false,
        phone,
        preferencesSurveySource,
        regionOptions,
        steps,
        survey
    } = params;
    const isParameterEntry = entry === "parameter";
    const showExitPrompt = !isParameterEntry && steps.some((wizardStep) => isHomeMatchPreferencesWizardSurveyStep(wizardStep) || wizardStep === SUMMARY);
    const dispatch = useAppDispatch();
    const [stepIndex, setStepIndex] = useState(0);
    const [isMapActive, setIsMapActive] = useState(false);
    const [editingStep, setEditingStep] = useState<HomeMatchPreferencesWizardSurveyStep | null>(null);
    const mapActionsRef = useRef<IHomeMatchPreferencesWizardMapActions | null>(null);
    const wasOpenRef = useRef(isOpen);
    const saveInFlightRef = useRef(false);
    const step = steps[stepIndex];
    const nextStep = steps[stepIndex + 1];
    const activeStep = editingStep ?? step;

    const surveyState = useHomeMatchPreferencesWizardSurvey({
        activeStep,
        amplitudeLocation,
        editingStep,
        financingPreference,
        isOpen,
        onResumeStep: (resumeStepIndex) => setStepIndex((currentStepIndex) => Math.max(currentStepIndex, resumeStepIndex)),
        preferencesSurveySource,
        regionOptions,
        saveInFlightRef,
        steps,
        survey
    });
    const {canContinue, formMethods, isSaving, saved} = surveyState;

    const advance = () => {
        if (nextStep) return setStepIndex(stepIndex + 1);

        onSurveyStepsDone();
    };

    const phoneState = useHomeMatchPreferencesWizardPhone({
        amplitudeLocation,
        onAdvance: advance,
        onClose,
        onDone: onSurveyStepsDone,
        phone,
        saveInFlightRef,
        trackSaved: surveyState.trackSaved
    });
    const summaryState = useHomeMatchPreferencesWizardSummary({
        hasNextStep: Boolean(nextStep),
        isSaving,
        onAdvance: advance,
        onDone: onSurveyStepsDone,
        patchSaved: surveyState.patchSaved,
        preferencesSurveySource,
        saveInFlightRef,
        trackSaved: surveyState.trackSaved
    });
    const {isChanging: isChangingPhone, isSubmitting: isPhoneSubmitting} = phoneState;
    const visibleStep = getVisibleHomeMatchPreferencesWizardStep({editingStep, isChangingPhone, step});
    const isSmsStepMounted = step === SMS && !editingStep;
    const isVisible = Boolean(step && (isHomeMatchPreferencesWizardParameterStep(step) || step === SUMMARY || step === PHONE || step === SMS));
    const progressStepIndex = editingStep ? steps.indexOf(editingStep) : stepIndex;

    if (isOpen && !wasOpenRef.current) {
        setStepIndex(getHomeMatchPreferencesWizardResumeStepIndex(steps, saved.request));
        setEditingStep(null);
        setIsMapActive(false);
        phoneState.reset();
        summaryState.resetConsent();
        saveInFlightRef.current = false;
        surveyState.resetToSaved();
    }

    wasOpenRef.current = isOpen;

    useEffect(() => {
        dispatch(setPhoneVerificationModalSuppressed(isOpen));

        return () => {
            dispatch(setPhoneVerificationModalSuppressed(false));
        };
    }, [dispatch, isOpen]);

    const registerMapActions = useCallback((actions: IHomeMatchPreferencesWizardMapActions | null) => {
        mapActionsRef.current = actions;
    }, []);

    const finishEditing = () => {
        setIsMapActive(false);
        setEditingStep(null);
    };

    const goNext = async () => {
        if (isSaving || !canContinue) return;

        const didSave = await surveyState.saveStep();

        if (!didSave) return;

        if (editingStep) return finishEditing();

        if (nextStep && (isHomeMatchPreferencesWizardSurveyStep(nextStep) || nextStep === SUMMARY)) return setStepIndex(stepIndex + 1);

        onSurveyStepsDone();
    };

    const goBack = () => {
        if (isChangingPhone) return phoneState.stopChanging();

        if (editingStep) {
            surveyState.resetToSaved();
            finishEditing();
            return;
        }

        if (returnsToSummary) return onClose();

        if (stepIndex === 0) return;

        setStepIndex(stepIndex - 1);
    };

    const closeWithoutSaving = () => {
        if (saveInFlightRef.current || isPhoneSubmitting) return;

        saveInFlightRef.current = true;
        onClose();
    };

    const saveAndClose = async () => {
        if (saveInFlightRef.current || isPhoneSubmitting) return;

        if (visibleStep === PHONE) return phoneState.savePhoneAndClose();

        if (visibleStep === SMS) return phoneState.closeSms();

        if (visibleStep === SUMMARY) {
            surveyState.trackSaved(SUMMARY);
            return onClose();
        }

        const didSave = await surveyState.saveStep();

        if (didSave) onClose();
    };

    const editSummaryStep = (summaryStep: HomeMatchPreferencesWizardSurveyStep) => {
        HomeMatchAmplitudeTracker.preferencesWizardClick(HomeMatchPreferencesWizardAmplitudeButton.EDIT, summaryStep, amplitudeLocation);
        setEditingStep(summaryStep);
    };

    const submitPhone = () => phoneState.submit(visibleStep);

    const getIsPrimaryDisabled = () => {
        switch (visibleStep) {
            case SUMMARY:
                return isSaving || summaryState.isSwitching;
            case SMS:
                return !phoneState.canConfirmSms || isPhoneSubmitting;
            case PHONE:
                return isPhoneSubmitting;
            default:
                return isSaving || !canContinue;
        }
    };

    const getOnPrimaryClick = () => {
        switch (visibleStep) {
            case SUMMARY:
                return summaryState.confirmSummary;
            case SMS:
            case PHONE:
                return submitPhone;
            default:
                return goNext;
        }
    };

    return {
        canGoBack: stepIndex > 0 || isChangingPhone || returnsToSummary || editingStep != null,
        editingStep,
        closeWithoutSaving,
        formMethods,
        goBack,
        isExitSaveDisabled: isSaving || isPhoneSubmitting,
        isParameterEntry,
        isPrimaryDisabled: getIsPrimaryDisabled(),
        showExitPrompt,
        isVisible,
        map: {
            actions: isMapActive ? {apply: () => mapActionsRef.current?.apply(), cancel: () => mapActionsRef.current?.cancel()} : undefined,
            isActive: isMapActive,
            registerActions: registerMapActions,
            setIsActive: setIsMapActive
        },
        onPrimaryClick: getOnPrimaryClick(),
        phone: phoneState.phone,
        primaryLabel: getHomeMatchPreferencesWizardPrimaryLabel({isParameterEntry, returnsToSummary, step: visibleStep}),
        progressStepIndex,
        saveAndClose,
        sms: {...phoneState.sms, isMounted: isSmsStepMounted},
        stepCount: steps.length,
        summary: {
            consent: summaryState.consent,
            onChangeStep: editSummaryStep,
            saved: {
                price: saved.request.price,
                regionIds: saved.request.regions,
                regionOptions: saved.regionOptions,
                rooms: saved.request.rooms
            },
            setConsent: summaryState.setConsent
        },
        visibleStep
    };
};
import {useCallback, useEffect, useRef, useState} from "react";

import {IOption} from "@pg-design/select-module";
import {PreferencesSurveySource} from "@pg-rp/common-types";

import {
    HomeMatchAmplitudeLocation,
    HomeMatchAmplitudeTracker,
    HomeMatchPreferencesWizardAmplitudeButton
} from "@/features/tracking/amplitude/home_match_amplitude_tracker";
import {setPhoneVerificationModalSuppressed} from "@/features/user/state/user_phone_verification_slice";
import {useAppDispatch} from "@/store/hooks/use_app_dispatch";

import {HomeMatchFinancingPreference} from "../../constants/preferences/home_match_preferences_values";
import {getHomeMatchPreferencesWizardPrimaryLabel} from "../../utils/get_home_match_preferences_wizard_primary_label";
import {
    getHomeMatchPreferencesWizardResumeStepIndex,
    getVisibleHomeMatchPreferencesWizardStep,
    HomeMatchPreferencesWizardEntry,
    HomeMatchPreferencesWizardStep,
    HomeMatchPreferencesWizardSurveyStep,
    IHomeMatchPreferencesWizardSurvey,
    isHomeMatchPreferencesWizardParameterStep,
    isHomeMatchPreferencesWizardSurveyStep
} from "../../utils/get_home_match_preferences_wizard_steps";
import {IHomeMatchPreferencesWizardMapActions} from "./use_home_match_preferences_wizard_location_map";
import {useHomeMatchPreferencesWizardPhone} from "./use_home_match_preferences_wizard_phone";
import {useHomeMatchPreferencesWizardSummary} from "./use_home_match_preferences_wizard_summary";
import {useHomeMatchPreferencesWizardSurvey} from "./use_home_match_preferences_wizard_survey";

const {PHONE, SMS, SUMMARY} = HomeMatchPreferencesWizardStep;

interface IParams {
    amplitudeLocation: HomeMatchAmplitudeLocation;
    entry?: HomeMatchPreferencesWizardEntry;
    financingPreference: HomeMatchFinancingPreference | null;
    isOpen: boolean;
    onClose: () => void;
    /** A step opened from the summary. Back and close return there without saving. */
    returnsToSummary?: boolean;
    onSurveyStepsDone: () => void;
    phone: string | null;
    preferencesSurveySource: PreferencesSurveySource;
    regionOptions: IOption<number>[];
    steps: HomeMatchPreferencesWizardStep[];
    survey: IHomeMatchPreferencesWizardSurvey | null;
}

export const useHomeMatchPreferencesWizardSession = (params: IParams) => {
    const {
        amplitudeLocation,
        entry = "homeMatch",
        financingPreference,
        isOpen,
        onClose,
        onSurveyStepsDone,
        returnsToSummary = false,
        phone,
        preferencesSurveySource,
        regionOptions,
        steps,
        survey
    } = params;
    const isParameterEntry = entry === "parameter";
    const showExitPrompt = !isParameterEntry && steps.some((wizardStep) => isHomeMatchPreferencesWizardSurveyStep(wizardStep) || wizardStep === SUMMARY);
    const dispatch = useAppDispatch();
    const [stepIndex, setStepIndex] = useState(0);
    const [isMapActive, setIsMapActive] = useState(false);
    const [editingStep, setEditingStep] = useState<HomeMatchPreferencesWizardSurveyStep | null>(null);
    const mapActionsRef = useRef<IHomeMatchPreferencesWizardMapActions | null>(null);
    const wasOpenRef = useRef(isOpen);
    const saveInFlightRef = useRef(false);
    const step = steps[stepIndex];
    const nextStep = steps[stepIndex + 1];
    const activeStep = editingStep ?? step;

    const surveyState = useHomeMatchPreferencesWizardSurvey({
        activeStep,
        amplitudeLocation,
        editingStep,
        financingPreference,
        isOpen,
        onResumeStep: (resumeStepIndex) => setStepIndex((currentStepIndex) => Math.max(currentStepIndex, resumeStepIndex)),
        preferencesSurveySource,
        regionOptions,
        saveInFlightRef,
        steps,
        survey
    });
    const {canContinue, formMethods, isSaving, saved} = surveyState;

    const advance = () => {
        if (nextStep) return setStepIndex(stepIndex + 1);

        onSurveyStepsDone();
    };

    const phoneState = useHomeMatchPreferencesWizardPhone({
        amplitudeLocation,
        onAdvance: advance,
        onClose,
        onDone: onSurveyStepsDone,
        phone,
        saveInFlightRef,
        trackSaved: surveyState.trackSaved
    });
    const summaryState = useHomeMatchPreferencesWizardSummary({
        hasNextStep: Boolean(nextStep),
        isSaving,
        onAdvance: advance,
        onDone: onSurveyStepsDone,
        patchSaved: surveyState.patchSaved,
        preferencesSurveySource,
        saveInFlightRef,
        trackSaved: surveyState.trackSaved
    });
    const {isChanging: isChangingPhone, isSubmitting: isPhoneSubmitting} = phoneState;
    const visibleStep = getVisibleHomeMatchPreferencesWizardStep({editingStep, isChangingPhone, step});
    const isSmsStepMounted = step === SMS && !editingStep;
    const isVisible = Boolean(step && (isHomeMatchPreferencesWizardParameterStep(step) || step === SUMMARY || step === PHONE || step === SMS));
    const progressStepIndex = editingStep ? steps.indexOf(editingStep) : stepIndex;

    if (isOpen && !wasOpenRef.current) {
        setStepIndex(getHomeMatchPreferencesWizardResumeStepIndex(steps, saved.request));
        setEditingStep(null);
        setIsMapActive(false);
        phoneState.reset();
        summaryState.resetConsent();
        saveInFlightRef.current = false;
        surveyState.resetToSaved();
    }

    wasOpenRef.current = isOpen;

    useEffect(() => {
        dispatch(setPhoneVerificationModalSuppressed(isOpen));

        return () => {
            dispatch(setPhoneVerificationModalSuppressed(false));
        };
    }, [dispatch, isOpen]);

    const registerMapActions = useCallback((actions: IHomeMatchPreferencesWizardMapActions | null) => {
        mapActionsRef.current = actions;
    }, []);

    const finishEditing = () => {
        setIsMapActive(false);
        setEditingStep(null);
    };

    const goNext = async () => {
        if (isSaving || !canContinue) return;

        const didSave = await surveyState.saveStep();

        if (!didSave) return;

        if (editingStep) return finishEditing();

        if (nextStep && (isHomeMatchPreferencesWizardSurveyStep(nextStep) || nextStep === SUMMARY)) return setStepIndex(stepIndex + 1);

        onSurveyStepsDone();
    };

    const goBack = () => {
        if (isChangingPhone) return phoneState.stopChanging();

        if (editingStep) {
            surveyState.resetToSaved();
            finishEditing();
            return;
        }

        if (returnsToSummary) return onClose();

        if (stepIndex === 0) return;

        setStepIndex(stepIndex - 1);
    };

    const closeWithoutSaving = () => {
        if (saveInFlightRef.current || isPhoneSubmitting) return;

        saveInFlightRef.current = true;
        onClose();
    };

    const saveAndClose = async () => {
        if (saveInFlightRef.current || isPhoneSubmitting) return;

        if (visibleStep === PHONE) return phoneState.savePhoneAndClose();

        if (visibleStep === SMS) return phoneState.closeSms();

        if (visibleStep === SUMMARY) {
            surveyState.trackSaved(SUMMARY);
            return onClose();
        }

        const didSave = await surveyState.saveStep();

        if (didSave) onClose();
    };

    const editSummaryStep = (summaryStep: HomeMatchPreferencesWizardSurveyStep) => {
        HomeMatchAmplitudeTracker.preferencesWizardClick(HomeMatchPreferencesWizardAmplitudeButton.EDIT, summaryStep, amplitudeLocation);
        setEditingStep(summaryStep);
    };

    const submitPhone = () => phoneState.submit(visibleStep);

    const getIsPrimaryDisabled = () => {
        switch (visibleStep) {
            case SUMMARY:
                return isSaving || summaryState.isSwitching;
            case SMS:
                return !phoneState.canConfirmSms || isPhoneSubmitting;
            case PHONE:
                return isPhoneSubmitting;
            default:
                return isSaving || !canContinue;
        }
    };

    const getOnPrimaryClick = () => {
        switch (visibleStep) {
            case SUMMARY:
                return summaryState.confirmSummary;
            case SMS:
            case PHONE:
                return submitPhone;
            default:
                return goNext;
        }
    };

    return {
        canGoBack: stepIndex > 0 || isChangingPhone || returnsToSummary || editingStep != null,
        editingStep,
        closeWithoutSaving,
        formMethods,
        goBack,
        isExitSaveDisabled: isSaving || isPhoneSubmitting,
        isParameterEntry,
        isPrimaryDisabled: getIsPrimaryDisabled(),
        showExitPrompt,
        isVisible,
        map: {
            actions: isMapActive ? {apply: () => mapActionsRef.current?.apply(), cancel: () => mapActionsRef.current?.cancel()} : undefined,
            isActive: isMapActive,
            registerActions: registerMapActions,
            setIsActive: setIsMapActive
        },
        onPrimaryClick: getOnPrimaryClick(),
        phone: phoneState.phone,
        primaryLabel: getHomeMatchPreferencesWizardPrimaryLabel({isParameterEntry, returnsToSummary, step: visibleStep}),
        progressStepIndex,
        saveAndClose,
        sms: {...phoneState.sms, isMounted: isSmsStepMounted},
        stepCount: steps.length,
        summary: {
            consent: summaryState.consent,
            onChangeStep: editSummaryStep,
            saved: {
                price: saved.request.price,
                regionIds: saved.request.regions,
                regionOptions: saved.regionOptions,
                rooms: saved.request.rooms
            },
            setConsent: summaryState.setConsent
        },
        visibleStep
    };
};
